// @ts-nocheck
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../helpers/hooks';
import { BadgeDisplay } from '../helpers/BadgeDisplay';
import { PointBadge } from '../helpers/PointBadge';
import { VerticalLine } from '../helpers/VerticalLine';
import { PlusIcon, CloseIcon } from '@iconicicons/react';
import toast from 'react-hot-toast';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetPathReportAssetsQuery,
  useCreatePathReportSequenceMutation,
} from '../services/edgeApi';
import { usePathBuilder } from '../pathBuilder/usePathBuilder';
import { PathTable } from './PathTable';
import { PathSelectorModal } from '../paths/PathSelectorModal';
import { UncontrolledTooltip } from 'reactstrap';
import { PathReportSelectionContext } from '../utils/pathReportState';
import { readLastState } from '../utils/pathReportState';

const SEPARATOR_HEIGHT = 24;

type Props = {
  showControls?: boolean;
  showTable?: boolean;
  scenario_id?: string;
  gas_date?: string;
};

export const PathBuilder: React.FC<Props> = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    points = [],
    addPoints,
    removePoint,
    newPointPosition,
    setNewPointPosition,
  } = usePathBuilder();
  const [theme] = useTheme();
  const loadedSequenceRef = useRef<string | null>(null);
  const [
    createPathReportSequence,
    {
      data: pathSequenceData,
      isLoading: isCreatingPath,
      error: pathSequenceError,
    },
  ] = useCreatePathReportSequenceMutation();

  const selectionCtx = React.useContext(PathReportSelectionContext);
  const lastState = React.useMemo(() => readLastState() || {}, []);

  const hasCtx = Boolean(selectionCtx);
  const scenarioId = hasCtx
    ? selectionCtx?.selected?.scenario_id
    : props.scenario_id ?? lastState.scenario_id ?? 'default';
  const gasDate = hasCtx
    ? selectionCtx?.selected?.gas_date
    : props.gas_date ??
      lastState.gas_date ??
      new Date().toISOString().slice(0, 10);

  const arg =
    scenarioId && gasDate
      ? { scenario_id: scenarioId, gas_date: gasDate }
      : skipToken;
  const {
    data: pathAssets,
    isFetching: assetsLoading,
    isError: assetsError,
  } = useGetPathReportAssetsQuery(arg);

  const seqKey = useMemo(() => {
    if (points.length < 2) return null;
    return points.map((p) => p.id).join(',');
  }, [points]);

  const onSubmit = useCallback(() => {
    if (!seqKey) return;

    const ids = points.map((p) => Number(p.id));
    setSearchParams({ sequence: ids.join(',') });

    createPathReportSequence({
      sequence: ids,
      best_contracts_only: true,
      scenario_id: scenarioId,
      gas_date: gasDate,
    });
  }, [
    seqKey,
    createPathReportSequence,
    points,
    scenarioId,
    gasDate,
    setSearchParams,
  ]);

  // Handle path sequence errors
  useEffect(() => {
    if (pathSequenceError) {
      toast.error('Failed to build path');
    }
  }, [pathSequenceError]);

  // In tab-only view (no controls), read sequence from URL and auto-build
  useEffect(() => {
    if (!props.showControls) {
      const seq = searchParams.get('sequence');
      if (seq) {
        const ids = seq
          .split(',')
          .map((s) => Number(s))
          .filter(Boolean);
        if (ids.length >= 2) {
          // Create a unique key for this sequence + scenario + date
          const sequenceKey = `${seq}-${scenarioId}-${gasDate}`;
          
          // Only call the API if we haven't already loaded this exact sequence
          if (loadedSequenceRef.current !== sequenceKey) {
            loadedSequenceRef.current = sequenceKey;
            createPathReportSequence({
              sequence: ids,
              best_contracts_only: true,
              scenario_id: scenarioId,
              gas_date: gasDate,
            });
          }
        }
      }
    }
  }, [
    props.showControls,
    searchParams,
    scenarioId,
    gasDate,
  ]);

  const lastPoint = useMemo(() => {
    if (
      newPointPosition === null ||
      newPointPosition < 1 ||
      newPointPosition > points.length ||
      points.length === 0
    )
      return null;
    return points[newPointPosition - 1];
  }, [points, newPointPosition]);

  const nextPointLockedTsp = useMemo(() => {
    if (lastPoint === null) return [];
    return [lastPoint.tsp_id];
  }, [lastPoint]);

  const derived = React.useMemo(() => {
    if (!pathAssets) return {};
    return {
      // ...existing code using pathAssets...
    };
  }, [pathAssets]);

  // Convert pathSequenceData to the expected format
  const builderSummary = useMemo(() => {
    if (!pathSequenceData) return [];
    return Array.isArray(pathSequenceData)
      ? pathSequenceData
      : pathSequenceData?.paths ?? [];
  }, [pathSequenceData]);

  return (
    <>
      {props.showControls && (
        <div style={{ position: 'relative' }}>
          <div
            className="d-flex flex-column justify-content-center align-items-center gap-2 pt-3 pb-2"
            style={{
              position: 'sticky',
              top: -16,
              backgroundColor: 'var(--bs-body-bg)',
              zIndex: 1,
            }}
          >
            <h3 className="text-center">Path</h3>
            <span id="submit-path-button">
              <button
                disabled={points.length < 2 || isCreatingPath}
                className="btn btn-primary"
                onClick={onSubmit}
              >
                {isCreatingPath ? 'Building Path...' : 'Submit'}
              </button>
            </span>
          </div>
          <div className="d-flex flex-column justify-content-center align-items-center mt-3 gap-2">
            <BadgeDisplay
              role="button"
              onClick={() => setNewPointPosition(0)}
              theme={theme}
            >
              <PlusIcon />
            </BadgeDisplay>

            {points.map((point, index) => (
              <React.Fragment key={point.id + '-' + index}>
                <VerticalLine
                  className="mx-3"
                  height={SEPARATOR_HEIGHT}
                  thickness={3}
                  stroke="dashed"
                />

                <PointBadge
                  point={point}
                  theme={theme}
                  extra_text={
                    <>
                      {point.tsp_name}
                      <br />
                      <CloseIcon
                        onClick={(e) => {
                          removePoint(index);
                          e.stopPropagation();
                        }}
                      />
                    </>
                  }
                />

                <VerticalLine
                  className="mx-3"
                  height={SEPARATOR_HEIGHT}
                  thickness={3}
                  stroke="dashed"
                />

                <BadgeDisplay
                  role="button"
                  onClick={() => setNewPointPosition(index + 1)}
                  theme={theme}
                >
                  <PlusIcon />
                </BadgeDisplay>
              </React.Fragment>
            ))}

            {points.length < 2 && (
              <UncontrolledTooltip
                placement={'bottom'}
                target="submit-path-button"
              >
                Please add at least two points to build a path
              </UncontrolledTooltip>
            )}
          </div>
        </div>
      )}
      {!props.showControls &&
        builderSummary.length === 0 &&
        !isCreatingPath && (
          <div className="alert alert-info">
            Click the "+" to select points to build a path.
          </div>
        )}
      {!props.showControls && isCreatingPath && (
        <div className="alert alert-info">Building path...</div>
      )}
      {props.showControls && (
        <PathSelectorModal
          startingLocation={
            lastPoint !== null
              ? `${lastPoint.tsp_name}: ${lastPoint.name}`
              : undefined
          }
          isOpen={newPointPosition !== null}
          lockedTsps={nextPointLockedTsp}
          onClose={() => setNewPointPosition(null)}
          onSelect={(selected) => {
            if (selected.points.some((point) => !point.node)) {
              toast.error("Can't find paths for unmapped points");
              return;
            }
            addPoints(selected.points, newPointPosition!);
            setNewPointPosition(points.length + selected.points.length);
          }}
        />
      )}
      {props.showTable && (
        <PathTable pathData={builderSummary} expandAll={true} />
      )}
    </>
  );
};
